<template>
  <VDialog
    ref="baseDialog"
    v-model="isOpen"
    data-testid="elementDialog"
    class="base-dialog"
    :width="!fullscreen ? sizeValue : undefined"
    :fullscreen="fullscreen"
    :scrollable="scrollable"
    :retain-focus="false"
  >
    <VCard
      ref="baseDialogCard"
      class="d-flex flex-column overflow-hidden pa-0"
      color="background"
    >
      <div
        id="base-dialog-fallback-focus"
        tabindex="-1"
      />
      <VCardTitle
        ref="baseDialogTitle"
        :class="['d-flex align-center px-6 pt-6 pb-4', { 'elevation-2': !isAtTop}]"
      >
        <VIcon
          v-if="headerIcon"
          ref="baseDialogHeaderIcon"
          class="mr-2"
          :color="headerIcon.color"
          :icon="headerIcon.icon"
          :size="headerIcon.size"
        />
        <div
          ref="baseDialogTitle"
          class="flex-grow-1 flex-shrink-1 text-h3 text-high-emphasis"
        >
          {{ title }}
        </div>
        <div
          v-if="timestamp"
          ref="baseDialogTimestamp"
          :class="[
            'ml-2',
            { 'mr-2': $slots.headerActions || closeButtonVisible }
          ]"
        >
          {{ format(timestamp, 'PP') }}
        </div>
        <!-- @slot Slot for additional actions in the dialog header -->
        <slot name="headerActions" />
        <VBtn
          v-if="closeButtonVisible"
          :ref="`baseDialogCloseButton`"
          data-testid="buttonDialogClose"
          color="background"
          icon
          size="small"
          elevation="0"
          @click="close"
        >
          <VIcon
            icon="close"
            size="24"
          />
        </VBtn>
      </VCardTitle>
      <VDivider
        v-if="titleBorder"
        class="pa-0 mb-6"
      />
      <VCardText
        id="scroll-target"
        ref="baseDialogText"
        :class="[
          'flex-grow-1 flex-shrink-1',
          {'px-6 pt-0 pb-6' : !contentUnstyled},
          {'pa-0' : contentUnstyled},
        ]"
      >
        <div
          v-scroll:#scroll-target="onScroll"
          class="h-100"
        >
          <!-- @slot Slot for the dialog content -->
          <slot />
        </div>
      </VCardText>
      <VCardActions
        v-if="$slots.actions"
        :ref="`baseDialogActions`"
        class="base-dialog__actions pa-0 d-block"
      >
        <VDivider />
        <div class="pa-6 d-flex align-center justify-end ga-2">
          <!-- @slot Slot for the dialog actions, typically used for buttons -->
          <slot name="actions" />
        </div>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';

import { format } from 'date-fns';

import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

const SIZES = {
  xsmall: 460,
  small: 600,
  medium: 900,
  large: 1200,
  xlarge: 1536,
} as const;
type Size = keyof typeof SIZES;

export interface HeaderIcon {
  color: string;
  icon: string;
  size: number;
}

const props = withDefaults(defineProps<{
  /** Whether to show the close button in the dialog header. */
  closeButtonVisible?: boolean;
  /** Whether to remove default padding from the dialog content. */
  contentUnstyled?: boolean;
  /** Whether the dialog should take up the full screen. */
  fullscreen?: boolean;
  /** Icon configuration for the dialog header. */
  headerIcon?: HeaderIcon;
  /** Controls the visibility of the dialog. */
  modelValue: boolean;
  /** Size of the dialog window. */
  size?: Size;
  /** Whether the dialog content should be scrollable. */
  scrollable?: boolean;
  /** Timestamp to display in the dialog header. */
  timestamp?: Date;
  /** Title text for the dialog header. */
  title?: string;
  /** Whether to show a border below the title. */
  titleBorder?: boolean;
}>(), {
  closeButtonVisible: true,
  contentUnstyled: false,
  fullscreen: false,
  headerIcon: undefined,
  modelValue: false,
  scrollable: true,
  size: 'medium',
  timestamp: undefined,
  title: undefined,
  titleBorder: false,
});

const isAtTop = ref(true);
const scrolledToBottom = ref(false);

const isOpen = defineModel<boolean>({ default: false });

const sizeValue = computed<number>(() => SIZES[props.size]);

const close = () => {
  isOpen.value = false;
};


const onScroll = (e: Event) => {
  const target = e.target as HTMLElement;
  isAtTop.value = target.scrollTop === 0;
  scrolledToBottom.value = target.scrollTop + target.clientHeight >= target.scrollHeight - 50;
};

const baseDialogCard = ref();

const focusTrap = useFocusTrap(baseDialogCard, {
  allowOutsideClick: (e: Event) => {
    return e.target !== baseDialogCard.value || baseDialogCard.value.contains(e.target as Node);
  },
  immediate: import.meta.env.TEST ? false : true,
  fallbackFocus: '#base-dialog-fallback-focus',
});

watch(isOpen, (val) => {
  if (val) {
    nextTick(() => {
      focusTrap.activate();
    });
  } else {
    focusTrap.deactivate();
  }
});

defineExpose({
  /**
   * Event handler for the scroll event.
   */
  onScroll,
  /**
   * Whether the dialog is at the top.
   */
  isAtTop,
  /**
   * Whether the dialog has been scrolled to the bottom.
   */
  scrolledToBottom,
});
</script>
